import React, { createContext, useContext, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";

import Header from "./CommonPage/Headers/Header";
import Footer from "./CommonPage/Footers/Footer";

import Banner from "./Componenets/BannerPage/Banner";
import { Discover } from "./Componenets/Discovers/Discover";
import GlobalAssets from "./Componenets/Globalassets/GlobalAssets";
import GlobalStage from "./Componenets/Globalassets/GlobalStage";
import Wealth from "./Componenets/Globalassets/Wealth";
import Ourinvest from "./Componenets/OurInvester/Ourinvest";
import { WhatsappTradings } from "./Componenets/WhatsAppTrading/WhatsappTradings";
import Account from "./Componenets/AccountType/Account";
import LandingPages from "./Pages/LandingPage/LandingPages";
import { TradingPage } from "./Pages/TradingPage/TradingPage";
import { StockDerivatives } from "./Pages/TradingPage/StockDerivatives";
import { Commonditity } from "./Pages/TradingPage/Commonditity";
import SubHeader from "./CommonPage/SubHeader/SubHeader";

import { Metals } from "./Pages/TradingPage/Metals";
import { Shares } from "./Pages/TradingPage/Shares";
import ScrollToTop from "./Componenets/ScrollToTop";
import { NewCrypto } from "./Pages/TradingPage/NewCrypto";
import { EquityNew } from "./Pages/TradingPage/EquityNew";
import { Energies } from "./Pages/TradingPage/Energies";
import Login from "./Layouts/LoginLayout/Login";
import LegalDocuments from "./Pages/Legal/LegalDocuments";
import Need from "./Componenets/NeedComponent/Need";
import HelpSupportMain from "./Componenets/help-support-section/help-support-main/HelpSupportMain";
import OurContact from "./Pages/Countactpage/OurContact";
import Cookies2 from "./CommonPage/Cookies2/Cookies2";
import Regulations from "./Pages/Legal/Regulations";
import PartnerWithUs from "./Componenets/PartnerPage/PartnerWithUs";
import MT5 from "./Pages/MT5/MT5";
import WhyBBCorp from "./Pages/WhyBBCorp/WhyBBCorp";
import CareersPage from "./Pages/CareersPage/CareersPage";
import LoginCard from "./Componenets/Login/LoginCard";
import Dialog from "@mui/material/Dialog";
import axios from "axios";

const SupportedLanguages = ["en", "ar", "fr"];

function RequireLang({ children }) {
  const { lang } = useParams();
  if (!SupportedLanguages.includes(lang)) {
    return <Navigate to="/en" replace />;
  }
  return children;
}

// Country redirector component
const CountryRedirector = () => {
  const [userCountry, setUserCountry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const detectAndRedirect = async () => {
      try {
        // Check if we already have country in localStorage
        const storedCountry = localStorage.getItem('userCountry');
        
        if (storedCountry) {
          setUserCountry(storedCountry);
          handleRedirect(storedCountry);
          return;
        }

        // Fetch country from API
        const response = await axios.get("https://ipwhois.app/json/");
        const countryCode = response.data.country;
        
        // Store country in localStorage
        localStorage.setItem('userCountry', countryCode);
        setUserCountry(countryCode);
        
        handleRedirect(countryCode);
      } catch (error) {
        console.error("Failed to fetch country:", error);
        // Default to no country code if API fails
        setUserCountry(null);
        handleRedirect(null);
      } finally {
        setIsLoading(false);
      }
    };

    const handleRedirect = (countryCode) => {
      const currentPath = location.pathname;
      const lang = localStorage.getItem('selectedLanguage') || 'en';
      
      // If we're already on a valid path, don't redirect
      if (currentPath.startsWith(`/${lang}`)) {
        return;
      }

      // If country is "lb", redirect to /:lang/lb/page
      if (countryCode === "LB") {
        const targetPath = currentPath === "/" ? `/${lang}/lb` : `/${lang}/lb${currentPath}`;
        navigate(targetPath, { replace: true });
      } else {
        // For all other countries, redirect to /:lang/page (no country code)
        const targetPath = currentPath === "/" ? `/${lang}` : `/${lang}${currentPath}`;
        navigate(targetPath, { replace: true });
      }
    };

    detectAndRedirect();
  }, [navigate, location]);

  if (isLoading) {
    return <div>Loading...</div>; // You can replace this with a proper loading component
  }

  return null;
};

const LayoutWrapper = () => {
  const location = useLocation();
  const { lang } = useParams();
  const path = window.location.pathname;
  const hideHeaderFooter = path === `/${lang}/login`;
  return (
    <>
      {!hideHeaderFooter && <Header />}
      <ScrollToTop />
      <Outlet />
      {!hideHeaderFooter && <Footer />}
    </>
  );
};

function AppRoutes() {
  // Helper to get lang from localStorage or default
  const getLang = () => {
    return localStorage.getItem('selectedLanguage') || 'en';
  };

  // Helper to get country from localStorage
  const getUserCountry = () => {
    return localStorage.getItem('userCountry');
  };

  // List of main pages to redirect
  const mainPages = [
    'partner',
    'account',
    'contact',
    'help',
    'forex-trading',
    'stock-derivatives',
    'commodities',
    'precious-metals',
    'shares',
    'cryptocurrencies',
    'equity',
    'energies',
    'legal',
    'regulations',
    'cookies',
    'mt5',
    'who-bbcorp',
    'discover',
    'global-assets',
    'global-stage',
    'wealth',
    'our-investors',
    'careers',
    // add more as needed
  ];

  return (
    <Routes>
      {/* Country redirector for root path */}
      <Route path="/" element={<CountryRedirector />} />
      
      {/* Redirect top-level main pages to appropriate format based on country */}
      {mainPages.map((page) => (
        <Route
          key={page}
          path={`/${page}`}
          element={
            <Navigate 
              to={
                getUserCountry() === "lb" 
                  ? `/${getLang()}/lb/${page}` 
                  : `/${getLang()}/${page}`
              } 
              replace 
            />
          }
        />
      ))}
      
      {/* Routes for /:lang (no country code) */}
      <Route path=":lang" element={<RequireLang><LayoutWrapper /></RequireLang>}>
        <Route index element={<LandingPages />} />
        <Route path="login" element={<Login />} />
        <Route path="banner" element={<Banner />} />
        <Route path="discover" element={<Discover />} />
        <Route path="global-assets" element={<GlobalAssets />} />
        <Route path="global-stage" element={<GlobalStage />} />
        <Route path="wealth" element={<Wealth />} />
        <Route path="our-investors" element={<Ourinvest />} />
        <Route path="whatsapp-trading" element={<WhatsappTradings />} />
        <Route path="account" element={<Account />} />
        <Route path="forex-trading" element={<TradingPage />} />
        <Route path="stock-derivatives" element={<StockDerivatives />} />
        <Route path="commodities" element={<Commonditity />} />
        <Route path="precious-metals" element={<Metals />} />
        <Route path="shares" element={<Shares />} />
        <Route path="cryptocurrencies" element={<NewCrypto />} />
        <Route path="equity" element={<EquityNew />} />
        <Route path="energies" element={<Energies />} />
        <Route path="legal" element={<LegalDocuments />} />
        <Route path="regulations" element={<Regulations />} />
        <Route path="contact" element={<OurContact />} />
        <Route path="cookies" element={<Cookies2 />} />
        <Route path="help" element={<Need />} />
        <Route path="partner" element={<PartnerWithUs />} />
        <Route path="mt5" element={<MT5 />} />
        <Route path="who-bbcorp" element={<WhyBBCorp />} />
        <Route path="careers" element={<CareersPage />} />
      </Route>
      
      {/* Routes for /:lang/lb (Lebanon country code) */}
      <Route path=":lang/lb" element={<RequireLang><LayoutWrapper /></RequireLang>}>
        <Route index element={<LandingPages />} />
        <Route path="login" element={<Login />} />
        <Route path="banner" element={<Banner />} />
        <Route path="discover" element={<Discover />} />
        <Route path="global-assets" element={<GlobalAssets />} />
        <Route path="global-stage" element={<GlobalStage />} />
        <Route path="wealth" element={<Wealth />} />
        <Route path="our-investors" element={<Ourinvest />} />
        <Route path="whatsapp-trading" element={<WhatsappTradings />} />
        <Route path="account" element={<Account />} />
        <Route path="forex-trading" element={<TradingPage />} />
        <Route path="stock-derivatives" element={<StockDerivatives />} />
        <Route path="commodities" element={<Commonditity />} />
        <Route path="precious-metals" element={<Metals />} />
        <Route path="shares" element={<Shares />} />
        <Route path="cryptocurrencies" element={<NewCrypto />} />
        <Route path="equity" element={<EquityNew />} />
        <Route path="energies" element={<Energies />} />
        <Route path="legal" element={<LegalDocuments />} />
        <Route path="regulations" element={<Regulations />} />
        <Route path="contact" element={<OurContact />} />
        <Route path="cookies" element={<Cookies2 />} />
        <Route path="help" element={<Need />} />
        <Route path="partner" element={<PartnerWithUs />} />
        <Route path="mt5" element={<MT5 />} />
        <Route path="who-bbcorp" element={<WhyBBCorp />} />
        <Route path="careers" element={<CareersPage />} />
      </Route>
      
      {/* Catch all other routes */}
      <Route path="*" element={<Navigate to="/en" replace />} />
    </Routes>
  );
}

// Create context for modal control
export const LoginCardModalContext = createContext(null);

function App() {
  const [loginCardOpen, setLoginCardOpen] = useState(false);
  const openLoginCardModal = () => setLoginCardOpen(true);
  const closeLoginCardModal = () => setLoginCardOpen(false);

  return (
    <LoginCardModalContext.Provider value={{ openLoginCardModal, closeLoginCardModal }}>
      <Router>
        <LanguageProvider>
          <AppRoutes />
          <Dialog open={loginCardOpen} onClose={closeLoginCardModal} maxWidth={false} PaperProps={{ sx: { borderRadius: 4, maxWidth: { xs: '98vw', sm: 480, md: 600, lg: 700 }, width: '100%' } }}>
            <LoginCard />
          </Dialog>
        </LanguageProvider>
      </Router>
    </LoginCardModalContext.Provider>
  );
}

export default App;
